import { useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';

/**
 * Enter a tenant, by whichever mechanism the READER has.
 *
 * ⚠️ Two ways in, and which applies is a fact about the reader rather than
 * about the tenant. Somebody holding a membership SWITCHES (`setTenant`); a
 * platform administrator holds none anywhere and must ASSUME it instead.
 *
 * Calling the wrong one is SILENT: `setTenant` refuses a tenant that is not
 * among the caller's own choices, so the control simply does nothing and the
 * reader presses it again. That is why this is a hook rather than a rule each
 * caller remembers — `BranchesCard` had it inline, and the moment the fleet
 * summary needed to reach another branch's vehicle there were two copies of it,
 * which is the shape this codebase keeps paying for.
 */
export function useEnterTenant(): (tenantId: string, name: string, isParent: boolean) => void {
  const { setTenant, assumeTenant, tenantChoices } = useAuth();

  return useCallback(
    (tenantId: string, name: string, isParent: boolean) => {
      const isMine = tenantChoices.some((c) => c.tenantId === tenantId);
      if (isMine) setTenant(tenantId);
      else assumeTenant({ id: tenantId, name, isParent });
    },
    [setTenant, assumeTenant, tenantChoices],
  );
}
